import type { dashboard } from '@cwos/api-client';
import type { SeriesPoint } from './charts.js';

/**
 * Reading the `disclosure` envelope that `/analytics/staff` and
 * `/analytics/schedule` attach to every response.
 *
 * Those two endpoints answer two audiences on one URL: a `dashboard:read`
 * caller gets the identified payload, an `analytics:read`-only caller gets a
 * de-identified variant with the per-worker roster omitted and any measure
 * standing on fewer than `minCohortSize` workers replaced by `null`.
 *
 * The rule these helpers exist to enforce: **an absence is never rendered
 * silently.** A withheld table must not degrade into an empty table, and a
 * suppressed measure must not degrade into `0` or a bare em dash. Both read as
 * "the product is broken" to somebody who was never told the number was
 * deliberately withheld. Every helper here returns the wording alongside the
 * value so the caller cannot render one without the other.
 *
 * Pure functions, deliberately: they are unit-testable without a DOM.
 */

type Disclosure = dashboard.AnalyticsDisclosure;
type SuppressedCell = dashboard.AnalyticsSuppressedCell;

/** True when the response omitted `field` entirely (not "returned it empty"). */
export function isWithheld(disclosure: Disclosure | undefined, field: string): boolean {
  return disclosure?.withheld.includes(field) ?? false;
}

/** The suppression block covering `field`, or null when the field was published. */
export function suppressedCell(
  disclosure: Disclosure | undefined,
  field: string,
): SuppressedCell | null {
  return disclosure?.suppression?.cells.find((c) => c.fields.includes(field)) ?? null;
}

const PLACEHOLDER = '—';

/**
 * WHAT A SUPPRESSED FIGURE READS AS, IN ONE PLACE.
 *
 * A WORD AND NOT A DASH, DELIBERATELY. `PLACEHOLDER` above is the ABSENT
 * state — nobody recorded the data — and the two must not share a glyph: an em
 * dash beside three populated tiles reads as a rendering fault or as "nothing
 * happened", which is the exact false sentence the floor exists to avoid. The
 * reader has to be able to see that something was deliberately not shown.
 */
export const SUPPRESSED_LABEL = 'Hidden';

/**
 * WHAT A METRIC WITH NO CONNECTED SOURCE READS AS — a THIRD glyph, distinct
 * from both neighbours on purpose.
 *
 * `SUPPRESSED_LABEL` ('Hidden') says a real figure exists and is being kept
 * back for privacy — the wrong sentence here, because there is no figure to
 * keep back. `PLACEHOLDER` ('—', below) is the ABSENT state for a question
 * that has no answer for this row (an em dash is what "no data" already means
 * on this screen). Neither is "this organization has no connected
 * time-tracking source, so 0.0h would be a measurement we did not make" — a
 * reader who sees `SUPPRESSED_LABEL` here would go looking for the permission
 * that unlocks it, and one who sees `PLACEHOLDER` would read a real, connected
 * zero. `metric.unavailableMessage` (server-written, `metricCatalog.
 * NO_SOURCE_MESSAGE`) is rendered beside this label, never re-derived.
 */
export const NOT_MEASURED_LABEL = 'Not measured';

/**
 * Is this figure the small-cohort suppression variant?
 *
 * `ServiceLevelSummary.utilization` and `.orgBenchmark` carry suppression as a
 * UNION MEMBER rather than as a `null` named in `disclosure.suppression`, so
 * that a screen with no branch for it cannot render it as a number. This is the
 * branch. It is the same state and the same floor as the envelope's cells —
 * only the carriage differs, because those two fields already had to be read
 * through `kind` to be rendered at all.
 */
export function isSmallCohortSuppressed(
  figure: { kind: string } | null | undefined,
): figure is dashboard.SmallCohortSuppressed {
  return figure?.kind === 'suppressed_small_cohort';
}

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

/** How a measure came to be missing — the three cases are not the same thing. */
export type MeasureState = 'value' | 'suppressed' | 'absent';

export interface MeasureDisplay {
  /** What goes in the tile. */
  value: string;
  /** Why, when there is no number. Always set for `suppressed`. */
  note?: string;
  state: MeasureState;
}

/**
 * Resolve one numeric measure for display.
 *
 * `null` means one of two very different things and the tile must not conflate
 * them: the figure was SUPPRESSED (it exists, we are not showing it, and
 * `disclosure.suppression` says why), or it is genuinely ABSENT (nobody has an
 * expected-hours plan, so there is no utilization to compute). Only the first
 * gets an explanation, because only the first has one.
 */
export function measure(
  value: number | null | undefined,
  field: string,
  disclosure: Disclosure | undefined,
  format: (v: number) => string,
): MeasureDisplay {
  if (value != null) return { value: format(value), state: 'value' };
  const cell = suppressedCell(disclosure, field);
  if (cell) {
    return {
      value: 'Hidden',
      note: suppressionReason(disclosure?.suppression?.minCohortSize),
      state: 'suppressed',
    };
  }
  return { value: PLACEHOLDER, state: 'absent' };
}

/**
 * Plain-language reason a cell is blank.
 *
 * NAMES THE FLOOR, NOT THE COHORT (F359). This used to read "only 3 people
 * behind this figure, and 5 are needed to show it", on the reasonable argument
 * that a person who can see "3 of the 5 needed" understands the blank while a
 * person who sees only "hidden" assumes a bug. The argument was right about
 * what the reader needs and wrong about what supplies it: the reader needs to
 * know the blank is a RULE rather than a fault, and the floor says that on its
 * own. The `3` said something else — an exact headcount, to a caller who
 * cannot see who those people are, about a window they chose to the day.
 *
 * So the sentence still distinguishes "hidden" from "we have no data", still
 * names the published constant that caused it, and no longer describes the
 * population. `plural` survives for the floor, which is the only count left.
 *
 * THE CELL PARAMETER IS GONE, deliberately. It was the only thing this function
 * needed the cohort from, and a parameter that still carries a secret nobody
 * reads is how the secret comes back. Dropping it makes every call site of this
 * sentence a compile error until somebody looks at it, which for a change of
 * this kind is the point.
 */
export function suppressionReason(minCohortSize: number | undefined): string {
  return minCohortSize == null
    ? 'Hidden: too few people are behind this figure to show it safely. It is hidden, not zero.'
    : `Hidden: figures standing on fewer than ${minCohortSize} ` +
        `${plural(minCohortSize, 'person', 'people')} are not shown. It is hidden, not zero.`;
}

/**
 * Plain-language reason a whole section is missing from the response. Driven by
 * `disclosure.withheld` so the copy cannot claim a field is withheld when the
 * server actually sent it.
 */
export function withheldReason(field: string, label: string): string {
  return (
    `${label} are not included in this view. Your access shows organisation-wide ` +
    `totals only — naming individual people requires the full dashboard permission. ` +
    `(Withheld: ${field}.)`
  );
}

/**
 * The one-line banner at the top of a de-identified screen, so the whole screen
 * is explained once rather than tile by tile. Null when nothing was held back.
 */
export function disclosureBanner(disclosure: Disclosure | undefined): string | null {
  if (!disclosure || disclosure.level !== 'aggregate') return null;
  const suppressedCount = disclosure.suppression?.cells.length ?? 0;
  const base = 'You are seeing the de-identified view: totals only, no individual people.';
  if (suppressedCount === 0) return base;
  return `${base} Some figures are also hidden because too few people stand behind them.`;
}

/**
 * ONE WEEKLY TREND SERIES, READY TO DRAW.
 *
 * THE ONE PLACE THE WIRE'S `TrendPoint` UNION IS READ. `/analytics/trends`
 * suppresses individual weekly points below `MIN_COHORT_SIZE` and carries the
 * refusal as `kind: 'suppressed_small_cohort'` — the same variant, the same
 * floor and the same sentence `ServiceLevelSummary.utilization` uses, because
 * it is the same state (section G of `docs/withholding-contract.md`).
 *
 * A SUPPRESSED POINT BECOMES `value: null` WITH THE SERVER'S SENTENCE BESIDE
 * IT, never `0`. This is the conversion the whole change exists to make
 * impossible to get wrong: a zero here would draw a DOWNWARD SPIKE — a chart
 * saying "activity collapsed that week" when it means "we will not tell you" —
 * which is a confident false statement rather than an over-disclosure, and so
 * strictly worse than the defect it replaced. `SeriesPoint` requires the
 * sentence, so the wrong shape does not compile.
 *
 * IT DOES NOT RE-DERIVE THE COPY. `message` is the server's, verbatim, for the
 * reason `SmallCohortSuppressed.message` gives: one sentence, in one place,
 * borrowing neither the withholding vocabulary ("not shown to you", which
 * sends a reader to ask for access that cannot help them) nor
 * `not_calculable`'s ("no data on file", which sends them to fill in a column
 * that is already filled in).
 */
export function trendPoints(series: dashboard.TrendSeries): SeriesPoint[] {
  return series.points.map((p) =>
    p.kind === 'value' ? { value: p.value, absence: '' } : { value: null, absence: p.message },
  );
}
